import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, ShoppingCart, ChevronDown, ChevronUp, Search, Ghost } from 'lucide-react';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('noora_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

function CartItemsList({ items }) {
    if (items.length === 0) return (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0' }}>Sepette ürün yok</div>
    );
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.map(item => {
                const img = item.product?.images?.[0]?.url;
                const price = item.variant?.price || item.product?.price;
                let variantLabel = '';
                if (item.variant?.attributes) {
                    try {
                        const attrs = Array.isArray(item.variant.attributes) ? item.variant.attributes : JSON.parse(item.variant.attributes || '[]');
                        variantLabel = attrs.map(a => `${a.name}: ${a.value}`).join(', ');
                    } catch {}
                }
                return (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '8px 10px' }}>
                        {img ? (
                            <img src={img.startsWith('http') ? img : `/uploads/${img}`} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                        ) : (
                            <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: 'var(--border)', flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.product?.name || 'Silinmiş ürün'}
                            </div>
                            {variantLabel && <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)' }}>{variantLabel}</div>}
                            {item.selectedSize && <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)' }}>Beden: {item.selectedSize}</div>}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--primary)' }}>
                                {price ? `₺${parseFloat(price).toFixed(2)}` : '-'}
                            </div>
                            <div style={{ fontSize: '0.77rem', color: 'var(--text-muted)' }}>x{item.quantity}</div>
                        </div>
                    </div>
                );
            })}
            <div style={{ textAlign: 'right', fontSize: '0.85rem', fontWeight: 700, paddingTop: '4px', color: 'var(--text-primary)' }}>
                Toplam: ₺{items.reduce((sum, item) => {
                    const p = parseFloat(item.variant?.price || item.product?.price || 0);
                    return sum + p * item.quantity;
                }, 0).toFixed(2)}
            </div>
        </div>
    );
}

export default function CustomersAdmin() {
    const [customers, setCustomers] = useState([]);
    const [guestCarts, setGuestCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(null);
    const [guestExpanded, setGuestExpanded] = useState(null);
    const [showGuests, setShowGuests] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get('/admin/customers'),
            api.get('/admin/customers/guest-carts')
        ]).then(([r1, r2]) => {
            setCustomers(r1.data);
            setGuestCarts(r2.data);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    const filtered = customers.filter(c => {
        const q = search.toLowerCase();
        return (
            c.firstName?.toLowerCase().includes(q) ||
            c.lastName?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.phone?.includes(q)
        );
    });

    const toggle = (id, setter) => setter(prev => prev === id ? null : id);

    const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d);
        return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div>
            <div className="admin-header">
                <div>
                    <h1>Müşteriler</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '2px' }}>
                        Kayıtlı müşteriler ve misafir sepetleri
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0 12px' }}>
                    <Search size={15} style={{ color: 'var(--text-muted)' }} />
                    <input
                        type="text"
                        placeholder="İsim, e-posta veya telefon ara..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ border: 'none', background: 'transparent', outline: 'none', padding: '8px 0', fontSize: '0.88rem', width: '220px', color: 'var(--text-primary)' }}
                    />
                </div>
            </div>

            {/* Kayıtlı Müşteriler */}
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>
                Kayıtlı Müşteriler ({filtered.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '2rem' }}>
                {filtered.map(c => {
                    const items = c.cart?.items || [];
                    const isOpen = expanded === c.id;
                    return (
                        <div key={c.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                            <div onClick={() => toggle(c.id, setExpanded)} style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', gap: '14px' }}>
                                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Users size={17} style={{ color: 'var(--primary)' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{c.firstName} {c.lastName}</div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '1px' }}>
                                        {c.email}{c.phone ? ` • ${c.phone}` : ''}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '5px',
                                        background: items.length > 0 ? 'rgba(108,92,231,0.1)' : 'var(--bg-secondary)',
                                        color: items.length > 0 ? 'var(--primary)' : 'var(--text-muted)',
                                        borderRadius: '20px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 600
                                    }}>
                                        <ShoppingCart size={13} />
                                        {items.length} ürün
                                    </div>
                                    <div style={{ color: 'var(--text-muted)' }}>
                                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </div>
                                </div>
                            </div>
                            {isOpen && (
                                <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                                        Kayıt: {formatDate(c.createdAt)}
                                        {' • '}{c.emailVerified ? '✓ E-posta doğrulandı' : '✗ Doğrulanmadı'}
                                        {' • '}{c.isActive ? 'Aktif' : 'Pasif'}
                                    </div>
                                    <CartItemsList items={items} />
                                </div>
                            )}
                        </div>
                    );
                })}
                {filtered.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>Müşteri bulunamadı</div>
                )}
            </div>

            {/* Misafir Sepetleri */}
            <div
                onClick={() => setShowGuests(p => !p)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px', userSelect: 'none' }}
            >
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Misafir Sepetleri ({guestCarts.length})
                </div>
                {showGuests ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
            </div>

            {showGuests && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {guestCarts.map(cart => {
                        const isOpen = guestExpanded === cart.id;
                        const items = cart.items || [];
                        return (
                            <div key={cart.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                                <div onClick={() => toggle(cart.id, setGuestExpanded)} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', gap: '12px' }}>
                                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(99,110,114,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Ghost size={16} style={{ color: 'var(--text-muted)' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                            Misafir
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: '1px' }}>
                                            {cart.sessionId}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            {formatDate(cart.updatedAt)}
                                        </div>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            background: 'rgba(108,92,231,0.1)', color: 'var(--primary)',
                                            borderRadius: '20px', padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600
                                        }}>
                                            <ShoppingCart size={12} />
                                            {items.length}
                                        </div>
                                        {isOpen ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />}
                                    </div>
                                </div>
                                {isOpen && (
                                    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                                        <CartItemsList items={items} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
